// parking-products-jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useContactForm } from "./ContactContext";

const machines = [
  {
    id: "ticketer",
    label: "Ticket Dispenser",
    title: "Automated Ticket Dispenser",
    description:
      "High-speed ticket dispensing system designed for high-traffic parking entrances. Delivers fast, reliable access control with thermal printing and barcode generation.",
    images: [
      "/images/m1.webp",
      "/images/parking-sec/p1.webp",
      "/images/parking-sec/p3.webp",
      "/images/parking-sec/p2.webp",
      { src: "/images/parking-sec/p4.webp", wide: true },
    ],
    specs: [
      { icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", label: "Thermal ticket printer built-in" },
      { icon: "M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z", label: "QR Code & Barcode support" },
      { icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z", label: "Anti-vandal stainless steel enclosure" },
      { icon: "M13 10V3L4 14h7v7l9-11h-7z", label: "Fast access — under 1 second response" },
    ],
    table: [
      { label: "Material", value: "Stainless Steel" },
      { label: "Voltage", value: "110V / 220V" },
      { label: "Display", value: '7" Touch Screen' },
      { label: "Connectivity", value: "TCP/IP, RS-232" },
    ],
    color: "#1a1f6c",
  },
  {
    id: "validator",
    label: "Validator",
    title: "Smart Ticket Validator",
    description:
      "Flexible validation terminal that allows merchants or staff to validate parking tickets at the point of sale — reducing friction and improving customer experience.",
    images: [
      "/images/m2.webp",
      "/images/parking-sec/p3.webp",
      { src: "/images/parking-sec/p4.webp", wide: true },
    ],
    specs: [
      { icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z", label: "One-touch validation for merchants" },
      { icon: "M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z", label: "Barcode & QR scanner" },
      { icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", label: "Real-time transaction reporting" },
      { icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z", label: "Secure encrypted validation" },
    ],
    table: [
      { label: "Material", value: "ABS + Metal" },
      { label: "Voltage", value: "110V" },
      { label: "Display", value: '4.3" Screen' },
      { label: "Connectivity", value: "WiFi / LAN" },
    ],
    color: "#1a1f6c",
  },
  {
    id: "exit",
    label: "Exit Terminal",
    title: "Automated Exit Terminal",
    description:
      "Full-featured exit payment station that accepts cash, credit cards, and QR codes. Handles ticket reading, fee calculation, and barrier control all in one unit.",
    images: [
      "/images/m3.webp",
      { src: "/images/parking-sec/p4.webp", wide: true },
    ],
    specs: [
      { icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z", label: "Cash / Credit Card / QR Code payment" },
      { icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", label: "Automatic fee calculation & receipt printing" },
      { icon: "M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z", label: "Integrated barrier gate control" },
      { icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z", label: '21.5" antiglare touch screen' },
    ],
    table: [
      { label: "Material", value: "Stainless Steel" },
      { label: "Voltage", value: "110V / 220V" },
      { label: "Display", value: '21.5" Touch' },
      { label: "Connectivity", value: "TCP/IP, RS-232" },
    ],
    color: "#1a1f6c",
  },
];

const AUTO_INTERVAL = 20000;

// Una imagen es un string o { src, wide }. Las horizontales (wide) se muestran
// completas (contain) sobre un fondo desenfocado de ella misma, para no recortarlas
// en el visor vertical.
const srcOf = (img) => (typeof img === "string" ? img : img.src);
const isWide = (img) => typeof img !== "string" && img.wide;

export const ParkingProducts = () => {
  const { open } = useContactForm();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-rotate entre máquinas
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % machines.length);
      setSelectedImage(0);
    }, AUTO_INTERVAL);
    return () => clearInterval(timer);
  }, [paused]);

  // Reset imagen al cambiar máquina
  useEffect(() => {
    setSelectedImage(0);
  }, [activeIndex]);

  const machine = machines[activeIndex];

  return (
    <section className="bg-white pt-10 pb-20 px-6 lg:px-8">
      <div className="max-w-screen-xl mx-auto">

        {/* Selector de máquinas */}
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-8 justify-center">
          {machines.map((m, i) => (
            <button
              key={m.id}
              onClick={() => {
                setActiveIndex(i);
                setSelectedImage(0);
                setPaused(true);
              }}
              className="relative px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300"
              style={{
                background: activeIndex === i ? "#1a1f6c" : "transparent",
                color: activeIndex === i ? "white" : "#6b7280",
                border: activeIndex === i ? "2px solid #1a1f6c" : "2px solid #e5e7eb",
              }}
            >
              {m.label}
              {/* Progress bar al fondo del botón activo */}
              {activeIndex === i && !paused && (
                <motion.div
                  className="absolute bottom-0 left-0 h-[2px] rounded-full bg-white/40"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: AUTO_INTERVAL / 1000, ease: "linear" }}
                  key={`bar-${i}`}
                />
              )}
            </button>
          ))}
          {/* Botón reanudar auto */}
          {paused && (
            <button
              onClick={() => setPaused(false)}
              className="px-4 py-2.5 rounded-full text-xs text-[#1a1f6c] border-2 border-dashed border-[#1a1f6c]/30 hover:border-[#1a1f6c] transition-colors duration-200"
            >
              ▶ Auto
            </button>
          )}
        </div>

        {/* Contenido con animación al cambiar */}
        <div className="relative overflow-hidden" style={{ minHeight: "560px" }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={machine.id}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40, position: "absolute", top: 0, left: 0, right: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start w-full"
          >
            {/* Gallery */}
            <div className="flex gap-4 w-full lg:w-auto">
              {/* Thumbnails: misma altura que el visor; si no caben, scroll vertical */}
              <div className="flex flex-col gap-4 flex-shrink-0 overflow-y-auto hide-scrollbar h-[320px] sm:h-[500px] md:h-[560px] lg:h-[520px]">
                {machine.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-[72px] h-[72px] sm:w-[90px] sm:h-[90px] rounded-xl overflow-hidden border-2 transition-all duration-200 flex-shrink-0 ${
                      selectedImage === index
                        ? "border-[#1a1f6c]"
                        : "border-transparent"
                    }`}
                  >
                    <img
                      src={srcOf(img)}
                      alt={`${machine.title}, view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Main image */}
              <div
                className="relative flex-1 lg:w-[420px] lg:flex-none h-[320px] sm:h-[500px] md:h-[560px] lg:h-[520px] rounded-2xl overflow-hidden bg-gray-100 cursor-zoom-in group"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  e.currentTarget.querySelectorAll("img").forEach((img) => { img.style.transformOrigin = `${x}% ${y}%`; });
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${machine.id}-${selectedImage}`}
                    className="absolute inset-0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    {isWide(machine.images[selectedImage]) && (
                      <img
                        src={srcOf(machine.images[selectedImage])}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60"
                      />
                    )}
                    <img
                      src={srcOf(machine.images[selectedImage])}
                      alt={machine.title}
                      className={`relative w-full h-full transition-transform duration-300 group-hover:scale-150 ${
                        isWide(machine.images[selectedImage]) ? "object-contain" : "object-cover"
                      }`}
                    />
                  </motion.div>
                </AnimatePresence>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="bg-black/40 rounded-full p-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Info con scroll */}
            <div className="flex-1 w-full lg:max-w-xl flex flex-col">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] leading-tight mb-2">
                {machine.title}
              </h2>
              <p className="text-sm sm:text-base text-[#6b7280] leading-relaxed mb-4">
                {machine.description}
              </p>

              <div
                className="overflow-y-auto pr-2 flex-1"
                style={{
                  maxHeight: "340px",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#1a1f6c22 transparent",
                }}
              >
                <ul className="space-y-3 mb-6">
                  {machine.specs.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#374151]">
                      <div className="w-8 h-8 rounded-lg bg-[#f0f2ff] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-[#1a1f6c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                        </svg>
                      </div>
                      <span className="leading-relaxed pt-1">{item.label}</span>
                    </li>
                  ))}
                </ul>

                <div className="rounded-xl overflow-hidden border border-gray-100 mb-4">
                  <div className="bg-[#f5f7ff] px-4 py-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#1a1f6c]">Specifications</p>
                  </div>
                  {machine.table.map((row, i) => (
                    <div key={i} className={`flex px-4 py-2.5 text-sm ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                      <span className="w-40 text-[#6b7280] font-medium flex-shrink-0">{row.label}</span>
                      <span className="text-[#111827]">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="in-line gap-4 mt-6 flex">
                <button
                  type="button"
                  onClick={() => open({ intent: "leasing", product: machine.title, industry: "parking", source: "Leasing Available" })}
                  className="mt-4 inline-flex items-center justify-center border border-[#1a1f6c] text-[#1a1f6c] font-medium rounded-lg px-7 py-3 hover:bg-[#1a1f6c]/5 transition-colors duration-300 w-fit"
                >
                  Leasing Available
                </button>
                <button
                  type="button"
                  onClick={() => open({ intent: "quote", product: machine.title, industry: "parking", source: "Request a Quote" })}
                  className="mt-4 inline-flex items-center justify-center bg-[#1a1f6c] text-white font-medium rounded-lg px-7 py-3 hover:bg-[#2832c2] transition-colors duration-300 w-fit"
                >
                  Request a Quote
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
        </div>
      </div>
    </section>
  );
};